// TODO: If you make edits to this file you must deal with these ESLint rules
/*eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/macro';

import {
  BREAKPOINTS,
  black,
  disabledColor,
  getColWidthCalc,
  primaryColor,
  unit,
  unitTRBL,
  white,
} from '@zego/theme';
import ThemePurple from '@zego/themes/ThemePurple';
import { TrackingScope } from '@zego/tracking';

import H1 from '@zego/components/atoms/H1';
import H2 from '@zego/components/atoms/H2';
import H3 from '@zego/components/atoms/H3';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import View from '@zego/components/atoms/View';
import ButtonLink from '@zego/components/button/ButtonLink';
import ContentContainer from '@zego/components/ContentContainer';
import CoverByWakam from '@zego/components/CoverByWakam';
import Logo from '@zego/components/header/Logo';
import HeaderDescription from '@zego/components/HeaderDescription';
import IrelandTaxiLeadFormHandler from '@zego/components/leadGeneration/IrelandTaxiLeadFormHandler';
import PromotionsHero from '@zego/components/promotions/Hero';
import { useScrollToElement } from '@zego/components/ScrollToElement';
import VerticalHairline from '@zego/components/VerticalHairline';

import LandingPageFooter from '../components/LandingPageFooter';

import TrustpilotColor from '@zego/static/images/TrustpilotColor.png';
import irelandHeroImage from '@zego/static/ireland/hero-ireland-taxi-freenow-landing.jpg';

const BLUE_COLOR = '#3af';
const BACKGROUND_COLOR = '#fafafa'; // from hero banner
const SCROLL_ID = 'ireland-landing-form';

export const IrelandTaxiBaseLandingPage = ({
  name,
  logo,
  title,
  dotInfo,
  subtitle,
  content,
  banner,
}) => (
  <TrackingScope locationName={name}>
    <ThemePurple>
      {banner}
      <LogoComponent productLogo={logo} />
      <PromotionsHero image={irelandHeroImage}>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>
        <CoverByWakam />
        <PromotionsHeroSpacer />
      </PromotionsHero>
      <Body>
        <FormColumn>
          <FormBox moveUp>
            <RoundInfo color={RoundInfo.BLUE}>{dotInfo}</RoundInfo>
            <IrelandTaxiLeadFormHandler buttonLabel="Find out more" />
          </FormBox>
          <TrustPilot />
          <Spacer height={unit(3)} />
          <RegularText>
            *10% of customers paid this or less in the last 6 months
          </RegularText>
        </FormColumn>
        <Content>
          {content}
          <Content.Paragraphs
            header={'Simply choose your level of cover and policy length:'}
            paragraphs={[
              {
                title: '30-day policy (Prices from €150/month*)',
                description: 'Short-term cover for as long as you need it.',
              },
              {
                title: 'Annual policy (Prices from €1,460/year*)',
                description: 'Cost-effective cover for full-time drivers.',
              },
              {
                title: 'Third-party only cover',
                description:
                  'Covers the costs for damaging other vehicles, but not yours.',
              },
              {
                title: 'Fully Comprehensive cover',
                description:
                  'Covers both the cost of damaging other vehicles as well as your own.',
              },
              {
                title: 'Optional Breakdown Cover for just €38 per year',
              },
            ]}
          />
          <Content.Sub2>Get your quote today!</Content.Sub2>
        </Content>
      </Body>
      <MoreDetails
        header={'Fill in your details and\nour team will be in touch.'}
        more="I’m interested"
      />
      <LandingPageFooter />
    </ThemePurple>
  </TrackingScope>
);

const PromotionsHeroSpacer = styled.div({
  marginBottom: 400,
  [BREAKPOINTS.medium]: {
    marginBottom: 80,
  },
  [BREAKPOINTS.normal]: {
    marginBottom: 20,
  },
});

export const Content = styled.div({
  margin: '40px 0 0',
  [BREAKPOINTS.medium]: {
    margin: '40px 50px 0 0',
  },
  [BREAKPOINTS.normal]: {
    margin: '40px 100px 0 0',
  },
});

Content.Sub1 = styled(H2)({
  fontSize: 18,
  lineHeight: '24px',
  fontWeight: 'bold',
  letterSpacing: 0,
  marginBottom: unit(6),
  [BREAKPOINTS.medium]: {
    fontSize: 18,
    lineHeight: '24px',
  },
  [BREAKPOINTS.small]: {
    fontSize: 18,
    fontWeight: 'bold',
  },
});

Content.Sub2 = styled(H3)({
  fontSize: 22,
  fontWeight: 'bold',
  letterSpacing: '-0.3px',
  lineHeight: '28px',
  marginBottom: unit(3),
  marginTop: unit(5),
  [BREAKPOINTS.medium]: {
    fontSize: 22,
    lineHeight: '28px',
  },
  [BREAKPOINTS.small]: {
    fontSize: 22,
    lineHeight: '28px',
  },
  '& > strong': {
    color: BLUE_COLOR,
    fontWeight: 700,
  },
});

Content.Text = styled(RegularText)({
  marginBottom: unit(5),
  fontSize: 18,
  lineHeight: '24px',
});

const StyledList = styled.ul({
  paddingLeft: unit(2),
  paddingBottom: unit(2),
  '& > li': {
    listStyle: 'disc',
    paddingLeft: unit(1),
    marginBottom: unit(2),
    fontSize: 18,
    lineHeight: '24px',
    '& > strong': {
      fontWeight: 700,
    },
  },
});

Content.List = ({ items }) => (
  <StyledList>
    {items.map(item => (
      <li key={item}>{item}</li>
    ))}
  </StyledList>
);

const Paragraph = ({ title, description }) => (
  <View>
    {title ? (
      <RegularText
        css={{
          color: disabledColor(10),
          fontWeight: 500,
          fontSize: '18px',
          lineHeight: '24px',
          textAlign: 'left',
          margin: '0 auto',
          [BREAKPOINTS.medium]: {
            width: '100%',
          },
        }}
      >
        {title}
      </RegularText>
    ) : null}
    {description ? (
      <RegularText
        css={{
          fontSize: '18px',
          lineHeight: '24px',
          color: disabledColor(8),
          marginBottom: unit(5),
          marginTop: unit(1),
          [BREAKPOINTS.medium]: {
            marginBottom: unit(3),
            marginTop: unit(1),
          },
        }}
      >
        {description}
      </RegularText>
    ) : null}
  </View>
);

Content.Paragraphs = ({ header, paragraphs }) => (
  <View>
    <RegularText
      css={{
        color: disabledColor(10),
        fontSize: '22px',
        lineHeight: '24px',
        fontWeight: 'bold',
      }}
    >
      {header}
    </RegularText>
    <Spacer height={unit(3)} />
    {paragraphs
      ? paragraphs.map(paragraph => (
          <Paragraph
            title={paragraph.title}
            description={paragraph.description}
          />
        ))
      : null}
  </View>
);

const Body = ({ children }) => (
  <View
    css={{
      background: BACKGROUND_COLOR,
    }}
  >
    <ContentContainer
      css={{
        paddingBottom: unit(4),
        [BREAKPOINTS.medium]: {
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row-reverse',
          paddingBottom: unit(5),
        },
      }}
    >
      {children}
    </ContentContainer>
  </View>
);

const Title = styled(H1)({
  color: white,
  fontSize: 32,
  letterSpacing: '-1.3px',
  marginBottom: unit(3),
  [BREAKPOINTS.medium]: {
    fontSize: '36px',
  },
});

const Subtitle = styled(RegularText)({
  color: white,
  fontSize: 18,
  fontWeight: 'bold',
  letterSpacing: '-0.73px',
  lineHeight: '23px',
  marginBottom: unit(3),
  [BREAKPOINTS.medium]: {
    fontSize: '16px',
    letterSpacing: '-0.94px',
    lineHeight: '28px',
  },
});

const LogoComponent = ({ productLogo }) => (
  <ContentContainer
    css={{
      zIndex: 1000,
      position: 'relative',
      marginBottom: unit(4),
      paddingTop: unit(3),
      [BREAKPOINTS.normal]: {
        paddingTop: unit(5),
      },
    }}
  >
    <View
      css={{
        flexGrow: 0,
        display: 'flex',
        alignItems: 'center',
        fill: white,
      }}
    >
      <Logo />
      <Spacer width={unit(2)} />
      <RegularText css={{ fontSize: 32, color: white, opacity: 0.8 }}>
        |
      </RegularText>
      <Spacer width={unit(2)} />
      {productLogo}
    </View>
  </ContentContainer>
);

export const ProductLogo = ({ top, src }) => (
  <img
    alt="Free Now"
    css={{
      display: 'inline-block',
      position: 'relative',
      top,
    }}
    src={src}
  />
);

const FormColumn = styled.div({
  marginBottom: unit(7),
});

const FormBox = ({ moveUp, children }) => (
  <View
    id={SCROLL_ID}
    css={{
      position: 'relative',
      backgroundColor: white,
      padding: unitTRBL(3.5, 2.5, 1.75, 2.5),
      borderRadius: '12px 0 12px 12px',
      marginTop: moveUp ? -350 : 0,
      [BREAKPOINTS.medium]: {
        marginLeft: 0,
        marginRight: 0,
        marginTop: moveUp ? -110 : 0,
        width: 440,
      },
    }}
  >
    {children}
  </View>
);

const RoundInfo = ({ color, children }) => (
  <View css={{ position: 'relative' }}>
    <View
      css={{
        position: 'absolute',
        zIndex: 100,
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: color,
        borderRadius: '8px 8px 0 0',
        height: 44,
        width: 'auto',
        right: -20,
        top: -78,
        padding: '25px 35px',
        [BREAKPOINTS.normal]: {
          padding: '27px 67px',
          borderRadius: 75,
          right: -74,
          top: -76,
          width: 132,
          height: 132,
        },
      }}
    >
      <RegularText
        css={{
          color: white,
          textAlign: 'center',
          lineHeight: '25px',
          letterSpacing: '0.4px',
          fontSize: 15,
          '& > strong': {
            fontSize: 20,
            whiteSpace: 'nowrap',
            display: 'block',
            [BREAKPOINTS.normal]: {
              display: 'block',
            },
          },
        }}
      >
        {children}
      </RegularText>
    </View>
  </View>
);
RoundInfo.BLUE = BLUE_COLOR;

const TrustPilot = () => (
  <View
    css={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: unit(7.5),
      [BREAKPOINTS.medium]: {
        paddingTop: unit(2.5),
      },
    }}
  >
    <img src={TrustpilotColor} alt="Trust Pilot Logo" css={{ width: 180 }} />
    <VerticalHairline
      css={{
        display: 'block',
        marginLeft: unit(3),
        marginRight: unit(3),
        borderLeft: `2px solid ${disabledColor(7)}`,
      }}
      height="43px"
    />
    <RegularText
      css={{
        fontSize: 17,
        color: black,
        lineHeight: '27px',
        position: 'relative',
        top: '4px',
        width: '204px',
      }}
    >
      <Trans id="Global / Trust Pilot Review Percentage">
        92% of our customers rate us 'great' or 'excellent'
      </Trans>
    </RegularText>
  </View>
);

const MoreDetails = ({ header, more }) => {
  const scrollSelector = `#${SCROLL_ID}`;
  const scrollHandler = useScrollToElement(scrollSelector);

  return (
    <View
      css={{
        backgroundColor: primaryColor(13),
        whiteSpace: 'pre-wrap',
      }}
    >
      <Spacer height={unit(7)} />
      <HeaderDescription
        header={header}
        headerColor={white}
        headerDesktopFontSize={32}
        headerMobileFontSize={32}
        headerLineHeight={'40px'}
        headerMobileLineHeight={'40px'}
      >
        <ButtonLink
          css={{
            backgroundColor: white,
            width: getColWidthCalc(12, 20, 10),
            borderColor: white,
            color: disabledColor(10),
            [BREAKPOINTS.medium]: {
              width: 'auto',
            },
          }}
          onClick={scrollHandler}
          href={`#${scrollSelector}`}
        >
          {more}
        </ButtonLink>
      </HeaderDescription>
      <Spacer height={unit(7)} />
    </View>
  );
};
